import Chatbot from "./components/page/chatbot/chatbot";
import React from "react";
import useBackendApi from "./hooks/useBackendApi";
import { AppContext, GenesysStatus } from "./store/genesys.context";
import { ThemeProvider } from "@emotion/react";
import { getTheme } from "./utils/theme/getTheme";
import { CircularProgress } from "@mui/material";
import MinimizedChatBubble from "./components/atoms/minimizedChatBubble.styled";
import debugLog from "./utils/debugLog";
import {
  disconnectFromBackend,
  GenesysLoadStatus,
} from "./api/backendConnector";
import type { ChatbotConfig } from "./lib/ChatbotConfig";

interface AppProps {
  config: ChatbotConfig;
}

const App = ({ config }: AppProps): React.JSX.Element => {
  const {
    connect,
  }: {
    connect: (onStatus?: (status: GenesysLoadStatus) => void) => void;
  } = useBackendApi();

  const {
    onReceiveMessage,
    initializeApp,
    initializeGenesys,
    genesysLoadAttempt,
    setGenesysStatus,
  }: {
    onReceiveMessage: () => void;
    initializeApp: boolean;
    initializeGenesys: boolean;
    genesysLoadAttempt: number;
    setGenesysStatus: React.Dispatch<React.SetStateAction<GenesysStatus>>;
  } = React.useContext(AppContext);

  // Load the Genesys SDK only once it has been requested — either lazily on the
  // first chat interaction or eagerly for returning users with an active
  // conversation (both set `initializeGenesys` in the context). This keeps the
  // heavy Genesys resources off the initial page load (SEO / Core Web Vitals).
  // `connect()` is idempotent; a retry resets the backend and bumps
  // `genesysLoadAttempt` to re-run this effect. The status callback drives the
  // error/retry hint on the welcome page.
  React.useEffect(() => {
    if (!initializeGenesys) return;

    debugLog("Loading Genesys SDK...");
    setGenesysStatus("loading");

    connect((status) => setGenesysStatus(status));
    onReceiveMessage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeGenesys, genesysLoadAttempt]);

  // Tear down the backend connection on unmount.
  React.useEffect(() => () => disconnectFromBackend(), []);

  return (
    <ThemeProvider theme={getTheme(config.brand)}>
      {/* The loading bubble is part of the launcher — with `hideLauncher` the
          host page owns the trigger, so nothing may flash in the corner while
          the app boots. */}
      {!initializeApp && !config.hideLauncher && (
        <MinimizedChatBubble>
          <CircularProgress
            sx={{
              color: (theme) =>
                theme.palette.widgetStyles.minimizedChatBubble.iconColor,
            }}
          />
        </MinimizedChatBubble>
      )}

      {initializeApp && <Chatbot />}
    </ThemeProvider>
  );
};

export default App;
